import { useMutation, useQueryClient } from "react-query"
import { generateClient } from "aws-amplify/data"
import { type Schema } from '@backend/data/resource'
import { Play, PlayFolder, UserLicense } from "./types";

const client = generateClient<Schema>();

const optimisticMutation = <DataType,>(key: string, queryClient) => {
  return {
    onMutate: async (newData: DataType) => {
    // Cancel any outgoing refetches
    // (so they don't overwrite our optimistic update)
    await queryClient.cancelQueries({ queryKey: [key] })

    // Snapshot the previous value
    const previousData = queryClient.getQueryData([
      key
    ])
    const newDataWithTimestamps = {
      ...newData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),}
    // Optimistically update to the new value
    if (previousData) {
      queryClient.setQueryData(
        [key],
        (old: DataType[] | undefined) => old ? [...old, newDataWithTimestamps] : [newDataWithTimestamps]
      )
    }

    // Return a context object with the snapshotted value
    return { previousData }
  },
  // If the mutation fails,
  // use the context returned from onMutate to rollback
  onError: (err, newData, context) => {
    console.error("Error saving record:", err, newData)
    if (context?.previousData) {
      queryClient.setQueryData(
        [key],
        context.previousData
      )
    }
  },
  // Always refetch after error or success:
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: [key] })
  }}
}

export const useSubmitPlayMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
  mutationFn: async (input: Play) => {
    if (input.id) {
      // Update existing play
      const { data: updatedPlay } = await client.models.Play.update({...input, id: input.id})
      return updatedPlay
    }
    const {
      data: newPlay
    } = await client.models.Play.create(input)
    return newPlay
  },
  ...optimisticMutation<Play>("plays", queryClient),
  })
}

export const useSavePlayFolderMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: PlayFolder) => {
      if (input.id) {
        // Update existing folder
        const { data: updatedFolder } = await client.models.PlayFolder.update({...input, id: input.id})
        return updatedFolder
      }
      const {
        data: newFolder
      } = await client.models.PlayFolder.create(input)
      return newFolder
    },
    ...optimisticMutation<PlayFolder>("playFolders", queryClient),
  })
}

export const useSubmitUserLicenseMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
  mutationFn: async (input: UserLicense) => {
    const {
      data
    } = await client.models.UserLicense.create(input)
    return data
  },
  ...optimisticMutation<UserLicense>("plays", queryClient),
  })
}


export const useDeletePlayMutation = () => {
  return useMutation(
    async (id: string) => {
      // const { data: deletedPlay, errors } = await client.models.Play.delete({id})
      await client.models.Play.delete({id})
      return Promise.resolve()
    }
  )
}

export const useDeletePlayFolderMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (folder: PlayFolder) => {
      const {
        data
      } = await client.models.PlayFolder.delete({ id: folder.id! })
      return data
    },
  })
}

export const useLicenseKeyMutation = () => {
  return useMutation(
  async ({licenseKey}: {licenseKey?: string} = {}) => {
    if (!licenseKey) {
      throw new Error("License key is required")
    }
    const {data, errors} = await client.mutations.useLicenseKey({ licenseKey })
    if (errors && errors.length > 0) {
      const message = errors.map(e => e.message).join(", ") || "Unknown error"
      throw new Error(message)
    }
    if (!data || data.result !== "success") {
      throw new Error(data?.message || "License key invalid!")
    }
    return data
  })
}

export const useStripePaymentMutation = (
  {planId, billingPeriod, returnUrl}: {planId: string, billingPeriod: "monthly" | "yearly", returnUrl: string }
) => {
  return useMutation(
  async () => {
    const { data, errors } = await client.mutations.stripePayment({
      purchaseType: planId as "pro" | "basic",
      billingPeriod,
      returnUrl
    })
    if (errors && !data?.clientSecret) {
      throw new Error(errors.map(e => e.message).join(", "))
    }
    if (!data?.clientSecret) {
      throw new Error("Something went wrong with starting the payment process!")
    }
    return data?.clientSecret
  })
}

export const useStripeCancelSubscriptionMutation = () => {
  return useMutation({
    mutationFn: async () => {
      const { data, errors } = await client.mutations.stripeCancelSubscription()
      if (errors && errors.length > 0) {
        throw new Error(errors.map(e => e.message).join(", "))
      }
      return data as { isCancelled: boolean } | null
    },
  })
}